/**
 * Audible market-session chimes + their nav-bar control.
 *
 * Mounted once in Layout, so it runs on every page of the dashboard.
 *
 * What it plays, on a regular session:
 *     09:30:00  opening bell         (real recording, ~10s)
 *     15:55:00  five-minute warning  (single soft chime, synthesised)
 *     15:58:00  two-minute warning   (double chime, synthesised)
 *     15:59:50  ten-second countdown (pips descending in pitch)
 *     16:00:00  closing bell         (the same recording, rung longer, ~15s)
 *
 * The open and close are excerpts of ONE real bell recording at the SAME pitch,
 * prepared by scripts/install_real_bell.py - as on a real floor, where one bell rings
 * at both ends of the day. They differ only in length. An earlier version dropped the
 * close a couple of semitones so the two could be told apart by ear; that was rejected
 * as unfaithful, so do not reintroduce it. The warnings and countdown pip remain
 * synthesised (scripts/render_chimes.py); they read as cues, not as bells.
 *
 * On an NYSE half day the close is 13:00 ET and every close-side cue shifts with it.
 * On weekends and holidays nothing fires.
 *
 * Chimes default to ON. Browsers still refuse to play audio before the user has
 * interacted with the page, so the first click or keypress anywhere in the dashboard
 * arms them; until that happens the control reports itself honestly rather than
 * claiming to be armed. "Not armed" has several causes with different remedies (a click,
 * a sound file that would not load, an audio output that will not start), and the panel
 * names the actual one - see chimePlayer.diagnose().
 *
 * SCOPE — read before extending this. These cues are a CLOCK, not a signal. They are
 * derived purely from the calendar and carry no information about the book, the tape,
 * or any recommendation. Nothing here may ever be wired to a signal, a ticket, or an
 * order: an audible cue that means "act" would be an execution prompt without an
 * approval gate. Keep it decorative.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bell, BellOff, TriangleAlert } from "lucide-react";

import {
  CHIME_LABELS,
  chimePlayer,
  COUNTDOWN,
  type ChimeHealth,
  type ChimeName,
} from "../lib/chimeAudio";
import {
  cuesToQueue,
  dueDiscreteCues,
  shouldQueueCountdown,
  shouldRingCloseAlone,
  WARN_2_MS,
  WARN_5_MS,
  type CueId,
} from "../lib/marketChimeSchedule";
import {
  formatEtTime,
  nextSessionBoundsFrom,
  sessionBoundsFor,
  type SessionBounds,
} from "../lib/marketSessionClock";

const LS_ENABLED = "helion.chimes.enabled";
const LS_VOLUME = "helion.chimes.volume";
const LS_FIRED_PREFIX = "helion.chimes.fired:";

/* ------------------------------------------------------------------ *
 * cross-tab de-duplication
 * ------------------------------------------------------------------ */

/** Random per-tab id, so two open dashboards do not both chime. */
const TAB_ID = Math.random().toString(36).slice(2, 10);

/**
 * Try to claim the right to fire `cue` for session `date`. Returns false if another
 * tab got there first.
 *
 * localStorage writes are synchronous per origin, so read-then-write is very nearly
 * atomic; the read-back check catches the residual interleaving. Worst case two tabs
 * chime at once, which sounds like one slightly louder chime — an acceptable failure
 * mode for a decorative cue, and much better than the alternative of five tabs
 * chiming five times.
 */
const OWNED_CLAIMS = new Set<string>();

function claimCue(date: string, cue: CueId): boolean {
  const key = `${LS_FIRED_PREFIX}${date}:${cue}`;
  // A claim we already hold stays ours. Without this, re-queueing after a suspend
  // would be refused by this tab's own claim and the cue would go silent.
  if (OWNED_CLAIMS.has(key)) return true;
  try {
    if (localStorage.getItem(key)) return false;
    localStorage.setItem(key, `${TAB_ID}:${Date.now()}`);
    const ours = (localStorage.getItem(key) ?? "").startsWith(`${TAB_ID}:`);
    if (ours) OWNED_CLAIMS.add(key);
    return ours;
  } catch {
    OWNED_CLAIMS.add(key);
    return true; // storage unavailable (private mode) — just play it
  }
}

/** Drop claim keys from previous sessions so localStorage does not grow forever. */
function pruneClaims(keepDate: string): void {
  try {
    const stale: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i);
      if (k && k.startsWith(LS_FIRED_PREFIX) && !k.startsWith(`${LS_FIRED_PREFIX}${keepDate}:`)) {
        stale.push(k);
      }
    }
    stale.forEach((k) => localStorage.removeItem(k));
  } catch {
    /* non-fatal */
  }
}

/* ------------------------------------------------------------------ *
 * schedule description (for the control panel)
 * ------------------------------------------------------------------ */

function describeSchedule(b: SessionBounds): string[] {
  if (b.closeMs == null || b.openMs == null) return [];
  const close = b.closeMs;
  return [
    `Opening bell — ${formatEtTime(b.openMs)} ET`,
    `Five-minute warning — ${formatEtTime(close - WARN_5_MS)} ET`,
    `Two-minute warning — ${formatEtTime(close - WARN_2_MS)} ET`,
    `Countdown — final ${COUNTDOWN.pips} seconds`,
    `Closing bell — ${formatEtTime(close)} ET`,
  ];
}

/* ------------------------------------------------------------------ *
 * why-not-armed copy
 * ------------------------------------------------------------------ */

function soundList(names: ChimeName[]): string {
  const labels = names.map((n) => CHIME_LABELS[n]);
  if (labels.length <= 1) return labels.join("");
  return `${labels.slice(0, -1).join(", ")} and ${labels[labels.length - 1]}`;
}

function notArmedLabel(h: ChimeHealth): string {
  switch (h.kind) {
    case "needs-click":
      return "Sound armed on your first click anywhere";
    case "audio-stuck":
      return "Audio output not responding — retrying";
    case "sounds-missing":
      return "Chime sounds did not load — retrying";
    case "unsupported":
      return "This browser cannot play the chimes";
    default:
      return "Loading chime sounds…";
  }
}

/** The remedy for each cause. Null while starting up, when there is nothing to do yet. */
function notArmedAdvice(h: ChimeHealth): string | null {
  const alsoMissing = h.missing.length
    ? ` The ${soundList(h.missing)} also did not load; the chimes fetch ${
        h.missing.length === 1 ? "it" : "them"
      } fresh and retry every minute on their own.`
    : "";
  switch (h.kind) {
    case "needs-click":
      return (
        "Your browser will not start audio until you interact with the page. Click anywhere " +
        "in the dashboard — or press a preview below — and the chimes arm. To skip this after " +
        "every reload, allow media autoplay for this site in the browser's settings." +
        alsoMissing
      );
    case "audio-stuck":
      return (
        "The browser may play sound here, but its audio output is not starting — this can " +
        "follow sleep, docking or a speaker reconnect. The chimes keep retrying on their own; " +
        "if it lasts, check the speakers are the system output, then restart the browser." +
        alsoMissing
      );
    case "sounds-missing":
      return (
        `Could not load the ${soundList(h.missing)}. The chimes fetch ${
          h.missing.length === 1 ? "it" : "them"
        } fresh from the server and retry every minute on their own — clicking will not help. ` +
        "If this lasts, clear the browser's cached images and files."
      );
    case "unsupported":
      return "This browser has no Web Audio support, so the chimes cannot play here.";
    default:
      return null;
  }
}

/* ------------------------------------------------------------------ *
 * component
 * ------------------------------------------------------------------ */

const MarketChimes: React.FC = () => {
  // Defaults ON. Only an ABSENT stored value counts as "never chosen" - an explicit
  // "0" means the operator turned it off and must keep winning over the default.
  const [enabled, setEnabled] = useState<boolean>(() => {
    try {
      const stored = localStorage.getItem(LS_ENABLED);
      return stored === null ? true : stored === "1";
    } catch {
      return true;
    }
  });
  const [volume, setVolume] = useState<number>(() => {
    try {
      const v = parseFloat(localStorage.getItem(LS_VOLUME) ?? "");
      return Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : 0.7;
    } catch {
      return 0.7;
    }
  });
  const [armed, setArmed] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [health, setHealth] = useState<ChimeHealth>(() => chimePlayer.diagnose());
  const [open, setOpen] = useState(false);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const healthKey = useRef("");

  // Called from the 1 Hz supervisor, so only a CHANGED diagnosis may re-render the nav.
  const syncHealth = useCallback(() => {
    const h = chimePlayer.diagnose();
    const key = `${h.kind}:${h.missing.join(",")}`;
    if (key === healthKey.current) return;
    healthKey.current = key;
    setHealth(h);
  }, []);

  const panelRef = useRef<HTMLDivElement | null>(null);
  const countdownQueuedFor = useRef<string | null>(null);
  // Read inside the supervisor without making it a dependency (which would tear down
  // and rebuild the interval every time the panel opened).
  const openRef = useRef(open);
  const lastRenderedMinute = useRef(-1);
  const prunedFor = useRef<string | null>(null);
  const resumeInFlight = useRef(false);
  // Cues this tab has already queued or fired for a given session date.
  const queuedFor = useRef<{ date: string; cues: Set<CueId> }>({ date: "", cues: new Set() });

  const todays = useMemo(() => sessionBoundsFor(nowMs), [nowMs]);
  const upcoming = useMemo(() => nextSessionBoundsFrom(nowMs), [nowMs]);

  /* ---- persistence ---- */
  useEffect(() => {
    try {
      localStorage.setItem(LS_ENABLED, enabled ? "1" : "0");
    } catch {
      /* non-fatal */
    }
  }, [enabled]);

  useEffect(() => {
    chimePlayer.setVolume(volume);
    try {
      localStorage.setItem(LS_VOLUME, String(volume));
    } catch {
      /* non-fatal */
    }
  }, [volume]);

  /* ---- arming / disarming ---- */
  const arm = useCallback(async (fromGesture: boolean) => {
    const ok = await chimePlayer.unlock(fromGesture);
    setArmed(ok && chimePlayer.ready);
    setBlocked(!ok);
    syncHealth();
    return ok;
  }, [syncHealth]);

  useEffect(() => {
    if (!enabled) {
      chimePlayer.suspend();
      countdownQueuedFor.current = null;
      queuedFor.current = { date: "", cues: new Set() };
      setArmed(false);
      return;
    }
    // Re-arming after a reload cannot use unlock() — there has been no gesture yet.
    // Try anyway: if the browser has an existing autoplay grant for this origin it
    // succeeds silently, and if not we render the honest "click to enable" state.
    arm(false);
  }, [enabled, arm]);

  /* ---- arm on the first interaction anywhere in the app ---- */
  // This is what makes "on by default" mean anything. Browsers refuse to start audio
  // until the user has interacted with the page, and the interaction does not have to
  // be with this component - so rather than requiring the operator to find the bell and
  // open its panel, ANY click or keypress in the dashboard arms the sound.
  //
  // Without this, the default-on state is a lie: the toggle reads On, the browser is
  // still blocking, and the 15:55 warning passes in silence. The effect tears itself
  // down as soon as `armed` flips true, so it costs nothing thereafter.
  useEffect(() => {
    if (!enabled || armed) return;
    let inFlight = false;

    const tryArm = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const ok = await chimePlayer.unlock(true);
        setArmed(ok && chimePlayer.ready);
        setBlocked(!ok);
        syncHealth();
      } finally {
        inFlight = false;
      }
    };

    document.addEventListener("pointerdown", tryArm);
    document.addEventListener("keydown", tryArm);
    return () => {
      document.removeEventListener("pointerdown", tryArm);
      document.removeEventListener("keydown", tryArm);
    };
  }, [enabled, armed, syncHealth]);

  /* ---- recover from laptop sleep / tab discard ---- */
  useEffect(() => {
    if (!enabled) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        chimePlayer.resumeIfNeeded().then(({ ok, wasSuspended }) => {
          setArmed(ok && chimePlayer.ready);
          setBlocked(!ok);
          if (wasSuspended) {
            // The audio clock froze while suspended, so anything queued against it
            // would now fire late in wall-clock terms — a closing chime at 16:10.
            // Drop it all; the supervisor re-queues from the real time on its next
            // tick, and only for cues that are genuinely still ahead.
            chimePlayer.cancelScheduled();
            queuedFor.current = { date: "", cues: new Set() };
            countdownQueuedFor.current = null;
          }
        });
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [enabled]);

  /* ---- the supervisor ---- */
  useEffect(() => {
    const tick = () => {
      const now = Date.now();

      // The supervisor has to run every second to catch cue boundaries, but it must not
      // re-render the whole nav at 1 Hz on every page for that. The displayed times are
      // minute-resolution, so while the panel is shut we re-render only when the minute
      // rolls over; while it is open we keep it live.
      const minute = Math.floor(now / 60_000);
      if (openRef.current || minute !== lastRenderedMinute.current) {
        lastRenderedMinute.current = minute;
        setNowMs(now);
      }

      if (!enabled) return;
      syncHealth();

      // SELF-HEAL. `ready` requires the AudioContext to be RUNNING, and a background
      // tab's context can be suspended by the OS or the browser at any time. Simply
      // returning here left the supervisor dead forever: the only recovery path was
      // visibilitychange, which fires when you switch TO the tab — so a dashboard left
      // in a background tab went permanently silent with no sign anything was wrong.
      // That is the exact reported failure: "the tab was up but nothing happened."
      //
      // No user gesture is needed to resume a context that was already unlocked once,
      // so try on every tick. The in-flight guard stops this from stacking up. The same
      // call re-fetches any sound that failed to load, so that also recovers unattended.
      if (!chimePlayer.ready) {
        if (!resumeInFlight.current) {
          resumeInFlight.current = true;
          chimePlayer
            .resumeIfNeeded()
            .then(({ ok, wasSuspended }) => {
              setArmed(ok && chimePlayer.ready);
              setBlocked(!ok);
              if (wasSuspended) {
                // The audio clock froze while suspended, so anything already queued
                // against it would fire late in wall-clock terms. Drop and re-derive.
                chimePlayer.cancelScheduled();
                queuedFor.current = { date: "", cues: new Set() };
                countdownQueuedFor.current = null;
              }
            })
            .finally(() => {
              resumeInFlight.current = false;
              syncHealth();
            });
        }
        return;
      }

      // Ready - possibly just now, via a background retry nobody clicked for. Nothing else
      // would flip the control to armed in that case. (A same-value set does not re-render.)
      setArmed(true);
      setBlocked(false);

      const bounds = sessionBoundsFor(now);
      if (bounds.openMs == null || bounds.closeMs == null) return; // weekend / holiday

      // Once per session, not once per second — pruning walks every localStorage key.
      if (prunedFor.current !== bounds.date) {
        prunedFor.current = bounds.date;
        pruneClaims(bounds.date);
      }

      // Per-session record of what this tab has already handled, so a cue queued
      // ahead of time is not also fired again by the fallback path.
      if (queuedFor.current.date !== bounds.date) {
        queuedFor.current = { date: bounds.date, cues: new Set() };
      }
      const handled = queuedFor.current.cues;

      // PRIMARY: hand the cue to the audio clock while it is still ahead of us. Immune
      // to background-tab throttling, which could otherwise step straight over the
      // narrow tolerance window and swallow the 15:55 warning entirely.
      cuesToQueue(now, bounds).forEach((c) => {
        if (handled.has(c.cue)) return;
        handled.add(c.cue);
        if (claimCue(bounds.date, c.cue)) chimePlayer.playAt(c.sound, c.atMs);
      });

      // FALLBACK: the instant already passed without being queued (page opened or
      // sound armed moments too late). Tolerance-bounded, so a page opened at 11:00
      // never replays the opening chime.
      dueDiscreteCues(now, bounds).forEach((c) => {
        if (handled.has(c.cue)) return;
        handled.add(c.cue);
        if (claimCue(bounds.date, c.cue)) chimePlayer.play(c.sound);
      });

      // The countdown and the closing bell are queued together onto the audio clock, so
      // their rhythm survives tab throttling. Queued once per session.
      const countdownKey = `${bounds.date}:countdown`;
      const alreadyQueued = countdownQueuedFor.current === countdownKey;

      if (!alreadyQueued && shouldQueueCountdown(now, bounds) && claimCue(bounds.date, "countdown")) {
        countdownQueuedFor.current = countdownKey;
        chimePlayer.scheduleCountdown(bounds.closeMs);
      } else if (
        shouldRingCloseAlone(now, bounds, alreadyQueued) &&
        claimCue(bounds.date, "countdown")
      ) {
        // The close slipped past without the countdown ever being queued (tab asleep,
        // or the sound armed a moment too late). Better a late bell than no bell.
        countdownQueuedFor.current = countdownKey;
        chimePlayer.play("close-bell");
      }
    };

    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [enabled, syncHealth]);

  /* ---- close the panel on outside click ---- */
  useEffect(() => {
    openRef.current = open;
    if (!open) return;
    setNowMs(Date.now()); // refresh the times the moment the panel is shown
    const onDown = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  /* ---- handlers ---- */
  const toggleEnabled = async () => {
    if (enabled) {
      setEnabled(false);
      return;
    }
    setEnabled(true);
    // This runs inside the click handler, which is what satisfies the autoplay policy.
    const ok = await arm(true);
    if (ok) chimePlayer.play("warn-5min", 0.9); // confirmation: if you hear it, it works
  };

  const preview = async (what: "open" | "close") => {
    if (!(await arm(true))) return;
    if (what === "open") chimePlayer.play("open-bell");
    else chimePlayer.previewCloseSequence();
  };

  /* ---- presentation ---- */
  const statusLabel = !enabled
    ? "Session chimes off"
    : blocked || !armed
      ? notArmedLabel(health)
      : todays.kind === "closed"
        ? `Armed — market closed today, next session ${upcoming.date}`
        : `Armed — ${todays.kind === "half" ? "half day, " : ""}close ${
            todays.closeMs != null ? formatEtTime(todays.closeMs) : "—"
          } ET`;

  const icon = !enabled ? (
    <BellOff size={16} />
  ) : blocked || !armed ? (
    <TriangleAlert size={16} />
  ) : (
    <Bell size={16} />
  );

  const iconColor = !enabled
    ? "var(--nav-text)"
    : blocked || !armed
      ? "var(--c-warn)"
      : "var(--accent-gold)";

  const scheduleLines = describeSchedule(todays.kind === "closed" ? upcoming : todays);
  const advice = enabled && (blocked || !armed) ? notArmedAdvice(health) : null;

  return (
    <div ref={panelRef} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label="Market session chimes"
        aria-expanded={open}
        title={statusLabel}
        className="rounded-md px-3 py-2 text-sm font-medium transition-colors"
        style={{
          backgroundColor: "var(--button-bg)",
          color: iconColor,
          border: "1px solid rgba(255, 255, 255, 0.55)",
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = "var(--button-bg-hover)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = "var(--button-bg)";
        }}
      >
        {icon}
      </button>

      {open && (
        <div
          style={{
            position: "absolute",
            right: 0,
            top: "calc(100% + 8px)",
            width: 310,
            zIndex: 60,
            backgroundColor: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: 10,
            boxShadow: "var(--shadow-soft)",
            padding: 14,
            fontSize: 13,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontWeight: 600 }}>Market session chimes</div>
            <button
              type="button"
              onClick={toggleEnabled}
              style={{
                fontSize: 12,
                fontWeight: 600,
                padding: "4px 12px",
                borderRadius: 999,
                cursor: "pointer",
                border: "1px solid var(--card-border)",
                backgroundColor: enabled ? "var(--status-positive-bg)" : "var(--bg-tertiary)",
                color: enabled ? "var(--status-positive-text)" : "var(--text-secondary)",
              }}
            >
              {enabled ? "On" : "Off"}
            </button>
          </div>

          <div style={{ marginTop: 8, fontSize: 11.5, color: "var(--text-secondary)" }}>
            {statusLabel}
          </div>

          {advice && (
            <div
              style={{
                marginTop: 10,
                padding: "7px 9px",
                borderRadius: 6,
                fontSize: 11.5,
                backgroundColor: "var(--status-warning-bg)",
                color: "var(--status-warning-text)",
                border: "1px solid var(--status-warning-border)",
              }}
            >
              {advice}
            </div>
          )}

          <div style={{ marginTop: 12 }}>
            <label
              htmlFor="chime-volume"
              style={{ fontSize: 11.5, color: "var(--text-secondary)" }}
            >
              Volume — {Math.round(volume * 100)}%
            </label>
            <input
              id="chime-volume"
              type="range"
              min={0}
              max={100}
              value={Math.round(volume * 100)}
              onChange={(e) => setVolume(Number(e.target.value) / 100)}
              style={{ width: "100%", marginTop: 4, accentColor: "var(--accent-gold)" }}
            />
          </div>

          <div style={{ marginTop: 10, display: "flex", gap: 8 }}>
            <button
              type="button"
              onClick={() => preview("open")}
              style={previewBtn}
            >
              Opening bell
            </button>
            <button
              type="button"
              onClick={() => preview("close")}
              style={previewBtn}
            >
              Close sequence
            </button>
          </div>

          {scheduleLines.length > 0 && (
            <div style={{ marginTop: 13, borderTop: "1px solid var(--card-border)", paddingTop: 10 }}>
              <div style={{ fontSize: 11, color: "var(--text-secondary)", marginBottom: 5 }}>
                {todays.kind === "closed"
                  ? `Next session — ${upcoming.date}${upcoming.kind === "half" ? " (half day)" : ""}`
                  : `Today${todays.kind === "half" ? " — half day, 13:00 close" : ""}`}
              </div>
              {scheduleLines.map((line) => (
                <div
                  key={line}
                  style={{ fontSize: 11.5, color: "var(--text-primary)", lineHeight: 1.6 }}
                >
                  {line}
                </div>
              ))}
            </div>
          )}

          <div
            style={{
              marginTop: 12,
              paddingTop: 9,
              borderTop: "1px solid var(--card-border)",
              fontSize: 10.5,
              color: "var(--text-secondary)",
              lineHeight: 1.5,
            }}
          >
            Calendar-driven only — these cues carry no signal, position, or recommendation.
            Scheduled US holidays and 13:00 half days are handled; an unscheduled market
            closure is not. Requires a dashboard tab left open.
          </div>
        </div>
      )}
    </div>
  );
};

const previewBtn: React.CSSProperties = {
  flex: 1,
  fontSize: 11.5,
  padding: "6px 8px",
  borderRadius: 6,
  cursor: "pointer",
  border: "1px solid var(--card-border)",
  backgroundColor: "var(--bg-tertiary)",
  color: "var(--text-primary)",
};

export default MarketChimes;
